import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import { useAuth } from '../../context/AuthContext';

// Chase only. Not staff, not other admins — isOwner is an email match, so
// granting someone is_shinyvault_admin never opens this door.
const day = (d) =>
  d ? new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : null;
const stamp = (t) => new Date(t).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

export default function AdminInfluencersPage() {
  const { isOwner, isLoading } = useAuth();
  const [people, setPeople] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isOwner) return;
    (async () => {
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, email, full_name, shinyvault_channel, shinyvault_influencer_since')
        .eq('is_shinyvault_influencer', true);
      const { data: rows } = await supabase
        .from('want_list_items').select('*')
        .eq('is_influencer', true).neq('status', 'cancelled')
        .order('created_at', { ascending: false });
      setPeople(profiles || []);
      setItems(rows || []);
      setLoading(false);
    })();
  }, [isOwner]);

  if (isLoading) return <p style={{ padding: 24 }}>Loading…</p>;
  if (!isOwner) return <Navigate to="/admin/orders" replace />;

  const forPerson = (id) => items.filter((i) => i.user_id === id);
  const orphans = items.filter((i) => !people.some((p) => p.id === i.user_id));

  return (
    <div style={{ padding: '8px 0 60px' }}>
      <h1 style={{ fontSize: '1.4rem', fontWeight: 900, marginBottom: 4 }}>Influencer area</h1>
      <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem', marginBottom: 24 }}>
        What creators say they will need, and when. Nothing here is priced — quote it when you buy it.
        Portal link: <code>/influencers</code>
      </p>

      {loading ? <p style={{ color: 'var(--text-muted)' }}>Loading…</p> : (
        <>
          {people.length === 0 && <p style={{ color: 'var(--text-muted)' }}>Nobody has come through the portal yet.</p>}

          {people.map((p) => {
            const mine = forPerson(p.id);
            return (
              <div key={p.id} style={{
                border: '1px solid var(--border)', borderRadius: 12, padding: '14px 16px', marginBottom: 14,
              }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
                  <b>{p.full_name || p.email}</b>
                  {p.shinyvault_channel && (
                    <span style={{ fontSize: '0.8rem', color: 'var(--accent)' }}>{p.shinyvault_channel}</span>
                  )}
                  <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginLeft: 'auto' }}>
                    {mine.length} item{mine.length === 1 ? '' : 's'}
                    {p.shinyvault_influencer_since ? ` · since ${stamp(p.shinyvault_influencer_since)}` : ''}
                  </span>
                </div>
                {p.full_name && (
                  <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>{p.email}</div>
                )}

                {mine.length === 0 ? (
                  <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginTop: 10 }}>
                    Signed in but has not added anything yet.
                  </p>
                ) : (
                  <div style={{ marginTop: 10 }}>
                    {mine.map((i) => (
                      <div key={i.id} style={{
                        display: 'flex', gap: 12, padding: '8px 0', borderTop: '1px solid var(--border)',
                        fontSize: '0.88rem',
                      }}>
                        <span style={{ flex: 1 }}>
                          {i.quantity > 1 && <span style={{ color: 'var(--text-muted)' }}>{i.quantity}× </span>}
                          {i.raw_text}
                          {i.note && (
                            <span style={{ display: 'block', fontSize: '0.76rem', color: 'var(--text-muted)' }}>
                              {i.note}
                            </span>
                          )}
                        </span>
                        <span style={{ fontSize: '0.76rem', color: 'var(--text-muted)', textAlign: 'right', minWidth: 190 }}>
                          {day(i.purchase_by) ? `by ${day(i.purchase_by)}` : 'no date'}
                          {day(i.content_date) ? ` · films ${day(i.content_date)}` : ''}
                          <span style={{ display: 'block' }}>added {stamp(i.created_at)}</span>
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}

          {orphans.length > 0 && (
            <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
              {orphans.length} item{orphans.length === 1 ? '' : 's'} from an account no longer flagged as an influencer.
            </p>
          )}
        </>
      )}
    </div>
  );
}
